import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { ActionStatus, WorkflowAction } from 'models/WorkflowAction';
import { WorkflowActionIntent } from 'services/api/workflows';

import { CompletedNote, InlineLink } from './categoryCard/styles';
import { ReferralModalSection } from './ReferralModal';

/**
 * The completed body for the document step, which has two faces.
 *
 * Per-document processing always completes: a failed document is not an action-level error or a
 * human gate, so the lifecycle status stays `completed` and the step stays terminal. What the API
 * withholds is the value — any failed document publishes no packet value at all, which is what
 * makes the extract and the clinical assessment flag for a person instead of settling against a
 * packet nobody said was incomplete. The API folds the dispatched batch's errored jobs into an
 * `errored` EFFECTIVE status so the red lands on the step that actually failed.
 *
 * That split is why this reads `effectiveStatus`. `isCompleted` is true either way, so the success
 * line would otherwise render underneath the row's own Error heading.
 *
 * It also has to own the retry. The card's other retry routes don't reach this state: the stale
 * banner needs `stale`, and there is no generic errored affordance keyed on the presented status
 * (behavior deliberately keys off `status` — see the WorkflowAction model). `rerun` alone is the
 * whole recovery here, with no chased `dispatch`: this kind is automated, so the evaluator
 * dispatches the fresh instance, and its dispatcher rebuilds an errored per-document job rather
 * than reusing it — so the same packet really is re-read, not waved through. A clean pass publishes
 * the full list, which unflags both consumers on the next evaluator pass.
 */
export default function ProcessAttachmentsNote({
  action,
  onEdit,
  onInvoke,
  isInvoking,
}: {
  action: WorkflowAction;
  onEdit?: (section: ReferralModalSection) => void;
  onInvoke: (actionId: string, intent: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const { canEditAction } = useWorkflowPermissions();
  const failed = action.effectiveStatus === ActionStatus.ERRORED;
  // Retrying the same packet is a different remedy from changing it: the documents may be right and
  // only the parse failed. Offered first for that reason — it's the cheaper thing to try. Inline
  // links joined with ' or ', matching how a flagged step offers its remedies.
  const retry = failed && canEditAction(action);

  return (
    <CompletedNote>
      {failed ? 'Some documents couldn’t be processed. ' : 'Documents have been successfully processed. '}
      {retry && (
        <InlineLink
          type='button'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'rerun')}
          data-testid='process-attachments-retry'>
          Try again
        </InlineLink>
      )}
      {retry && onEdit && ' or '}
      {/* Not merely disabled — an editor-less user gets no route in at all (OLIO-2615). */}
      {onEdit && (
        <InlineLink type='button' onClick={() => onEdit('documents')} data-testid='add-documents'>
          Add or remove documents
        </InlineLink>
      )}
    </CompletedNote>
  );
}
